import React, { createContext, ReactNode, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FullscreenIframeCloseButton,
    FullscreenIframeContent,
    FullscreenIframeOverlay,
    FullscreenIframeTopBar,
    FullscreenIframeTopBarLeft,
    FullscreenIframeTopBarRight
} from 'src/components/widgets/fullscreenIframeLayout';
import { Icon } from 'src/components/widgets/icon';
import { SectionTitle } from 'src/components/widgets/headers';
import { SectionDescription } from 'src/components/widgets/section/sectionDescription';
import { SectionHeader } from 'src/components/widgets/section/sectionHeader';
import { EarlyAccessTag } from 'src/components/widgets/tag';
import { backofficeUrl, T } from 'src/shared/helpers';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { useIsBulkExportEnabled } from './shared';

interface BulkImportFullscreenLayoutProps {
    children: ReactNode;
}

// Trim the top padding so the section content sits closer to the top bar now that the
// page header is gone. Also tighten the section headers within the section: SectionTitle
// is an <h5> that carries the browser's default top/bottom margins, which pushed the title
// down and opened a wide gap before the caption.
const Content = styled(FullscreenIframeContent)`
    padding-top: 8px;

    ${SectionHeader} {
        padding-top: 12px;
    }

    /* SectionTitle renders an <h5> that carries the browser's default top/bottom margins.
       Target the element directly (not just the styled class) so the title sits at the top
       of the header and right above its caption. */
    ${SectionHeader} > h5,
    ${SectionTitle} {
        margin: 0 0 2px;
    }

    ${SectionDescription} {
        margin-top: 0;
        padding-top: 0;
    }
`;

// Centered title + guide link in the top bar, mirroring the "3D Preview Setup" header.
const TopBarCenter = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
`;

const TopBarCenterTitle = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
`;

const TopBarCenterLink = styled.a`
    font-size: 11px;
    color: #3b82f6;
    text-decoration: none;
    &:hover {
        color: #2563eb;
    }
`;

// Section navigation (Home / Import / Export / Task history), kept in the top bar so
// every page of the section can move between the sub-flows now that the sidebar and
// the page header are hidden in fullscreen.
const TopBarNav = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const NavTab = styled.button<{ $active?: boolean }>`
    border: none;
    background: ${(props) => (props.$active ? '#eef2ff' : 'transparent')};
    color: ${(props) => (props.$active ? '#1e293b' : '#64748b')};
    font-size: 13px;
    font-weight: ${(props) => (props.$active ? 600 : 500)};
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${(props) => (props.$active ? '#eef2ff' : '#f1f5f9')};
    }
`;

// Fixed action bar pinned to the bottom of the overlay. Steps portal their Back/Next
// buttons here (via <BulkImportFooter>) instead of rendering them at the bottom of the
// scrolling section, so the primary actions stay visible. Hidden when no step uses it.
const FooterBar = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 24px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;

    > * {
        flex: 1 1 auto;
        margin: 0 !important;
    }

    &:empty {
        display: none;
    }
`;

const BulkImportFooterContext = createContext<HTMLElement | null>(null);

// Renders its children into the fixed footer bar of the surrounding fullscreen layout.
// Returns null until the footer element is mounted (first paint) — harmless, the step
// re-renders into it on the next commit.
export function BulkImportFooter({ children }: { children: ReactNode }) {
    const footerEl = useContext(BulkImportFooterContext);
    if (!footerEl) return null;
    return createPortal(children, footerEl);
}

// Renders the whole Bulk Product Setup (CSV) section as a fullscreen overlay on top
// of the app chrome (sidebar + header), mirroring the test-mode fullscreen pattern.
// The close button returns to the Customizable Products list, which is the only entry
// point to this section.
export default function BulkImportFullscreenLayout({ children }: BulkImportFullscreenLayoutProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const isExportEnabled = useIsBulkExportEnabled();
    const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);

    const path = location.pathname;
    const navItems = [
        { label: T._('Home', 'Admin'), url: backofficeUrl('/tools/bulk-import'), isActive: /\/tools\/bulk-import\/?$/.test(path) },
        { label: T._('Import', 'Admin'), url: backofficeUrl('/tools/bulk-import/wizard'), isActive: path.includes('/tools/bulk-import/wizard') },
        ...(isExportEnabled
            ? [{ label: T._('Export', 'Admin'), url: backofficeUrl('/tools/bulk-import/export/wizard'), isActive: path.includes('/tools/bulk-import/export') }]
            : []),
        {
            label: T._('Task history', 'Admin'),
            url: backofficeUrl('/tools/bulk-import/history'),
            isActive: path.includes('/tools/bulk-import/history') || path.includes('/tools/bulk-import/task')
        }
    ];

    return (
        <FullscreenIframeOverlay>
            <FullscreenIframeTopBar>
                <FullscreenIframeTopBarLeft>
                    <TopBarNav>
                        {navItems.map((item) => (
                            <NavTab key={item.url} type='button' $active={item.isActive} onClick={() => navigate(item.url)}>
                                {item.label}
                            </NavTab>
                        ))}
                    </TopBarNav>
                </FullscreenIframeTopBarLeft>
                <TopBarCenter>
                    <TopBarCenterTitle>
                        {T._('Bulk Product Setup (CSV)', 'Admin')}
                        <EarlyAccessTag>{T._('Early Access', 'Admin')}</EarlyAccessTag>
                    </TopBarCenterTitle>
                    <TopBarCenterLink href='https://zakeke.zendesk.com/hc/en-us' target='_blank' rel='noopener noreferrer'>
                        {T._('How to use', 'Admin')}
                    </TopBarCenterLink>
                </TopBarCenter>
                <FullscreenIframeTopBarRight>
                    <FullscreenIframeCloseButton onClick={() => navigate(backofficeUrl('/products'))} aria-label={T._('Close', 'Admin')}>
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </FullscreenIframeCloseButton>
                </FullscreenIframeTopBarRight>
            </FullscreenIframeTopBar>
            <BulkImportFooterContext.Provider value={footerEl}>
                <Content>{children}</Content>
            </BulkImportFooterContext.Provider>
            <FooterBar ref={setFooterEl} />
        </FullscreenIframeOverlay>
    );
}
